/**
 * MVP-555. "Does the focused element ITSELF do something with this key?" — the operator-side
 * companion to `lib/isTypingTarget.ts`, and deliberately NOT a change to it.
 *
 * `isTypingTarget` answers a different question ("is the user typing prose here?") and is shared
 * with the guest surface, which pins by identity that exactly one definition exists
 * (`features/guest/isTypingTarget.test.ts`). Widening it would silently change the guest's `/`
 * hotkey, its digit pad (MVP-274 R1) and its arrow-key result walk (MVP-491) as a side effect of an
 * operator fix, so the extra knowledge lives here, in `features/ops/`, where only the cockpit pays
 * for it.
 *
 * Two reported symptoms, one root: the hotkey table claimed keys that the focused control already
 * owned (Space on a focused button fired play/pause instead of pressing the button), and
 * `isTypingTarget` disowned keys that the focused control did NOT own (a focused `<input
 * type="checkbox" role="switch">` — every rotation pause control — killed `n`, `s` and the whole
 * panel because its `tagName` is `INPUT`).
 */

/** The parts of an element this reads. A real `HTMLElement` satisfies it; so does a plain object in
 * a test, which is why `getAttribute` is optional and `role` can be given directly. */
export interface KeyTargetLike {
  tagName?: string;
  type?: string;
  role?: string | null;
  disabled?: boolean;
  getAttribute?: (name: string) => string | null;
}

/** The parts of a `KeyboardEvent` this reads. */
export interface KeyChordLike {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
}

function attribute(target: KeyTargetLike, name: string): string | null {
  const value = target.getAttribute?.(name);
  if (typeof value === 'string') return value;
  return null;
}

function roleOf(target: KeyTargetLike): string {
  return (attribute(target, 'role') ?? target.role ?? '').toLowerCase();
}

/** `disabled` (or `aria-disabled`) flips the whole answer: a disabled button does NOT activate on
 * Space, so nothing would happen at all if the hotkey table stepped aside for it. A key pressed at
 * a dead control belongs to the app. */
function isDisabled(target: KeyTargetLike): boolean {
  if (target.disabled === true) return true;
  return attribute(target, 'aria-disabled') === 'true';
}

/** INPUT types that are a CONTROL, not a field to type into — the exemption `useHotkeys` applies to
 * `isTypingTarget`, which can only see `tagName === 'INPUT'`. Anything not listed here (text,
 * search, number, date, range, …) stays a typing target: the conservative miss is a hotkey that
 * does not fire, which is exactly today's behaviour. */
const CONTROL_INPUT_TYPES = new Set(['button', 'submit', 'reset', 'checkbox', 'radio']);

/**
 * True when the target is an `<input>` that is a control rather than a place to type. Used ONLY to
 * narrow `isTypingTarget` at the operator call site — see the ordering comment in `useHotkeys.ts`.
 */
export function isControlInput(target: KeyTargetLike | null | undefined): boolean {
  if (!target) return false;
  if (target.tagName?.toUpperCase() !== 'INPUT') return false;
  const type = (attribute(target, 'type') ?? target.type ?? 'text').toLowerCase();
  return CONTROL_INPUT_TYPES.has(type);
}

/** Things that activate on Space AND Enter: real buttons, ARIA buttons, `<summary>`, and the
 * button-like input types. Links are deliberately NOT here — see `isLink`. */
function isActivatable(target: KeyTargetLike): boolean {
  const tag = target.tagName?.toUpperCase();
  if (tag === 'BUTTON' || tag === 'SUMMARY' || roleOf(target) === 'button') return true;
  if (!isControlInput(target)) return false;
  const type = (attribute(target, 'type') ?? target.type ?? '').toLowerCase();
  return type === 'button' || type === 'submit' || type === 'reset';
}

/** A link follows on Enter and does NOTHING on Space — the browser scrolls instead, which is a
 * default MVP-495 cancels on purpose. So a link owns Enter only: lumping it in with buttons above
 * would hand Space back to nobody and lose the play/pause binding for as long as a link has focus.
 * A bare `<a>` with no href is not focusable and activates nothing — it is plain text. */
function isLink(target: KeyTargetLike): boolean {
  if (roleOf(target) === 'link') return true;
  return target.tagName?.toUpperCase() === 'A' && attribute(target, 'href') !== null;
}

/** Things that toggle on Space only — Enter does nothing to a checkbox. `role="switch"` is how
 * `ui/Switch.tsx` presents itself, and it is the pause control of every rotation slot. */
function isToggle(target: KeyTargetLike): boolean {
  if (roleOf(target) === 'switch' || roleOf(target) === 'checkbox' || roleOf(target) === 'radio') return true;
  if (!isControlInput(target)) return false;
  const type = (attribute(target, 'type') ?? target.type ?? '').toLowerCase();
  return type === 'checkbox' || type === 'radio';
}

/** A radio in a group is walked with the arrow keys — that is how it is SET from a keyboard at all,
 * the same reason `isTypingTarget` counts `<select>`. The cockpit ships no radio group today
 * (Settings uses `SegmentedControl`, whose items are `role="tab"`-style buttons), so this is
 * forward cover rather than a live fix; it is cheap and ↑/↓ are bound to tempo, which would move
 * the song under someone choosing an option. */
function isRadio(target: KeyTargetLike): boolean {
  if (roleOf(target) === 'radio') return true;
  const type = (attribute(target, 'type') ?? target.type ?? '').toLowerCase();
  return target.tagName?.toUpperCase() === 'INPUT' && type === 'radio';
}

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * True when `event`'s key is one the focused element itself operates, so the hotkey table must not
 * claim it. Everything else still reaches the table: pressing `n` on a focused button must still
 * skip the song, which is the whole reason this is per-KEY rather than per-ELEMENT.
 *
 * A chord is never yielded. `Ctrl+Z` is the app's undo no matter what has focus — no button, link
 * or checkbox does anything with it.
 */
export function targetOwnsKey(target: KeyTargetLike | null | undefined, event: KeyChordLike): boolean {
  if (!target) return false;
  if (event.ctrlKey === true || event.altKey === true || event.metaKey === true) return false;
  if (isDisabled(target)) return false;
  if (isActivatable(target)) return event.key === ' ' || event.key === 'Enter';
  if (isLink(target)) return event.key === 'Enter';
  if (isToggle(target)) {
    if (event.key === ' ') return true;
    return isRadio(target) && ARROW_KEYS.has(event.key);
  }
  return false;
}
